import React, { useState, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const ProductTabs = ({ product = {} }) => {
  const [activeTab, setActiveTab] = useState('description');
  const [reviews, setReviews] = useState([]);
  const [reviewsLoaded, setReviewsLoaded] = useState(false);

  useEffect(() => {
    if (!product?.id) return;
    let cancelled = false;
    setReviewsLoaded(false);
    import('../../../lib/wp-api').then(({ getProductReviews }) => {
      getProductReviews(product.id, { per_page: 20 }).then((rows) => {
        if (cancelled) return;
        setReviews(rows);
        setReviewsLoaded(true);
      });
    });
    return () => { cancelled = true; };
  }, [product?.id]);
  const descRef = useRef(null);

  // Post-process the description HTML: pull EVERY <img> out of its wrapping
  // <p>/<figure>/etc. and place them all into a single horizontal-scrolling
  // carousel inserted at the position of the first image. Text content stays
  // in its original flow. Mirrors the main product gallery UX so all the
  // packaging shots / size charts / lifestyle images live in one swipeable
  // row instead of stacking vertically.
  useEffect(() => {
    const root = descRef.current;
    if (!root) return;
    // Re-init: clear any previous wrappers + scrollers
    root.querySelectorAll(':scope > .oh-img-scroller-wrap, :scope > .oh-img-scroller').forEach(n => n.remove());

    const imgs = Array.from(root.querySelectorAll('img'));
    if (imgs.length === 0) return;

    // Walk up to find the top-level anchor (so we insert at the right place)
    let anchor = imgs[0];
    while (anchor.parentNode && anchor.parentNode !== root) anchor = anchor.parentNode;

    // Build wrapper that contains the scroller + prev/next arrow buttons.
    // Relative positioning so arrows can absolute-position over the scroller.
    const wrap = document.createElement('div');
    wrap.className = 'oh-img-scroller-wrap relative -mx-4 my-4';

    const scroller = document.createElement('div');
    scroller.className =
      'oh-img-scroller flex overflow-x-auto gap-4 snap-x snap-mandatory ' +
      'py-4 px-4 scroll-smooth';
    scroller.style.scrollbarWidth = 'thin';
    wrap.appendChild(scroller);

    // Arrow buttons
    const CELL_PAGE = 336; // w-80 (320) + gap-4 (16)
    const makeArrow = (dir, label, pathD) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.setAttribute('aria-label', label);
      btn.className =
        'oh-scroller-arrow absolute top-1/2 -translate-y-1/2 z-10 ' +
        (dir === 'prev' ? 'left-2 ' : 'right-2 ') +
        'h-9 w-9 rounded-full bg-white/95 shadow-md border border-black/5 ' +
        'flex items-center justify-center hover:bg-white transition ' +
        'disabled:opacity-0 disabled:pointer-events-none';
      btn.innerHTML =
        '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" ' +
        'viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" ' +
        'stroke-linecap="round" stroke-linejoin="round">' +
        '<path d="' + pathD + '"/></svg>';
      btn.addEventListener('click', () => {
        scroller.scrollBy({ left: dir === 'prev' ? -CELL_PAGE : CELL_PAGE, behavior: 'smooth' });
      });
      return btn;
    };
    const prevBtn = makeArrow('prev', 'Previous image', 'M15 18l-6-6 6-6');
    const nextBtn = makeArrow('next', 'Next image',     'M9 18l6-6-6-6');
    wrap.appendChild(prevBtn);
    wrap.appendChild(nextBtn);

    // Disable arrows at scroll bounds — update on scroll + on resize
    const updateArrows = () => {
      const max = scroller.scrollWidth - scroller.clientWidth - 1;
      prevBtn.disabled = scroller.scrollLeft <= 0;
      nextBtn.disabled = scroller.scrollLeft >= max;
    };
    scroller.addEventListener('scroll', updateArrows, { passive: true });

    // Insert the wrap into the flow + move all images into the scroller
    anchor.parentNode.insertBefore(wrap, anchor);
    imgs.forEach((img) => {
      const cell = document.createElement('div');
      cell.className = 'flex-none w-80 snap-start';
      const oldParent = img.parentNode;
      img.parentNode.removeChild(img);
      img.style.maxWidth = '100%';
      img.style.height = 'auto';
      img.style.display = 'block';
      img.style.borderRadius = '8px';
      img.loading = 'lazy';
      cell.appendChild(img);
      scroller.appendChild(cell);
      if (oldParent && oldParent !== root &&
          (oldParent.textContent || '').trim() === '' &&
          oldParent.querySelectorAll('img,video,svg').length === 0) {
        oldParent.parentNode?.removeChild(oldParent);
      }
    });

    // Initial arrow state (once images are placed)
    updateArrows();
  }, [product?.description, activeTab]);

  const defaultProduct = {};

  const productData = { ...defaultProduct, ...product };

  // (reviews state declared at top — fetched from WC Store API)


  const tabs = [
  { id: 'description', label: 'Description', icon: 'FileText' },
  { id: 'sizing',      label: 'Size Chart', icon: 'Ruler' },
  { id: 'care',        label: 'Care', icon: 'Sparkles' },
  { id: 'reviews', label: 'Reviews', icon: 'Star', count: reviews?.length },
  { id: 'specifications', label: 'Specifications', icon: 'Settings' },
  { id: 'shipping', label: 'Shipping', icon: 'Truck' }];


  const renderStars = (rating) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars?.push(
        <Icon
          key={i}
          name="Star"
          size={14}
          className={i <= rating ? "text-warning fill-current" : "text-muted-foreground"} />

      );
    }
    return stars;
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'description':
        return (
          <div className="prose prose-sm max-w-none">
            <div className="whitespace-pre-line text-foreground leading-relaxed">
              <div ref={descRef} className="prose prose-sm max-w-none break-words overflow-wrap-anywhere [&_img]:max-w-full [&_img]:h-auto [&_*]:max-w-full [&_p]:break-words [&_b]:break-words [&_table]:block [&_table]:overflow-x-auto" style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }} dangerouslySetInnerHTML={{ __html: productData?.description || "" }} />
            </div>
          </div>);


      case 'sizing':
        return <SizeChartGrid sizeChart={productData?.sizeChart} />;


      case 'care':
        return (
          <div>
            <h4 className="font-medium text-foreground mb-3">Care Instructions</h4>
            <div className="whitespace-pre-line text-sm text-muted-foreground leading-relaxed">
              {productData?.care || 'Machine wash cold with similar colors. Do not bleach. Tumble dry low or hang to dry. Iron on low if needed. Refer to the garment label for fabric-specific guidance.'}
            </div>
          </div>);


      case 'reviews': {
        const avgRating = typeof productData?.rating === 'number' && productData.rating > 0
          ? productData.rating
          : (reviews.length
              ? reviews.reduce((a, r) => a + (Number(r.rating) || 0), 0) / reviews.length
              : 0);
        const reviewCount = productData?.reviewCount ?? reviews.length;
        return (
          <div className="space-y-6">
            {/* Review Summary */}
            <div className="flex items-center justify-between pb-4 border-b border-border">
              <div className="flex items-center space-x-4">
                <div className="text-3xl font-bold text-foreground">{avgRating ? avgRating.toFixed(1) : '—'}</div>
                <div>
                  <div className="flex items-center space-x-1 mb-1">
                    {renderStars(Math.round(avgRating))}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {reviewCount > 0
                      ? `Based on ${reviewCount} review${reviewCount === 1 ? '' : 's'}`
                      : 'No reviews yet — be the first to review this product.'}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  alert('Review submission is coming soon — reviews you leave will appear here and in the merchant\'s WP dashboard.');
                }}
                className="text-sm text-primary hover:underline"
              >
                Write a review
              </button>
            </div>
            {/* Reviews List */}
            <div className="space-y-6">
              {reviewsLoaded && reviews.length === 0 && (
                <p className="text-sm text-muted-foreground italic">No reviews yet — be the first to share your thoughts.</p>
              )}
              {reviews?.map((review) =>
              <div key={review?.id} className="border-b border-border pb-6 last:border-b-0">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="flex items-center space-x-2 mb-1">
                        <span className="font-medium text-foreground">{review?.reviewer || review?.author || 'Anonymous'}</span>
                        {review?.verified &&
                      <span className="bg-success/10 text-success text-xs px-2 py-1 rounded-full flex items-center space-x-1">
                            <Icon name="CheckCircle" size={12} />
                            <span>Verified</span>
                          </span>
                      }
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="flex items-center space-x-1">
                          {renderStars(review?.rating)}
                        </div>
                        <span className="text-sm text-muted-foreground">{review?.formatted_date_created || review?.date_created || ""}</span>
                      </div>
                    </div>
                  </div>
                  
                  <h5 className="font-medium text-foreground mb-2">{review?.title || ""}</h5>
                  <p className="text-sm text-foreground mb-3 leading-relaxed">
                    <span dangerouslySetInnerHTML={{ __html: review?.review || review?.content || "" }} />
                  </p>
                  
                  {review?.images && review?.images?.length > 0 &&
                <div className="flex space-x-2 mb-3">
                      {review?.images?.map((image, index) =>
                  <div key={index} className="w-16 h-16 rounded-lg overflow-hidden">
                          <Image
                      src={image?.url}
                      alt={image?.alt}
                      className="w-full h-full object-cover" />

                        </div>
                  )}
                    </div>
                }
                  
                  <div className="flex items-center space-x-4 text-sm">
                    <button className="flex items-center space-x-1 text-muted-foreground hover:text-foreground">
                      <Icon name="ThumbsUp" size={14} />
                      <span>Helpful ({review?.helpful})</span>
                    </button>
                    <button className="text-muted-foreground hover:text-foreground">
                      Reply
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>);
      }


      case 'specifications': {
        // Build spec rows from real WC product fields when available; fall back
        // to any merchant-supplied productData.specifications object.
        const rows = {};
        if (productData?.sku) rows['SKU'] = productData.sku;
        if (productData?.weight) rows['Weight'] = productData.weight;
        if (productData?.dimensions) rows['Dimensions'] = productData.dimensions;
        if (productData?.brand) rows['Brand'] = productData.brand;
        if (Array.isArray(productData?.categories) && productData.categories.length) {
          rows['Category'] = productData.categories.map(c => c?.name || c).filter(Boolean).join(', ');
        }
        // Merge any merchant-defined extra specs
        if (productData?.specifications && typeof productData.specifications === 'object') {
          Object.assign(rows, productData.specifications);
        }
        const entries = Object.entries(rows);
        if (entries.length === 0) {
          return (
            <p className="text-sm text-muted-foreground">No specifications listed for this product yet.</p>
          );
        }
        return (
          <div className="space-y-4">
            {entries.map(([key, value]) => (
              <div key={key} className="flex justify-between py-2 border-b border-border last:border-b-0">
                <span className="font-medium text-foreground">{key}</span>
                <span className="text-muted-foreground text-right">{String(value)}</span>
              </div>
            ))}
          </div>);
      }


      case 'shipping': {
        const shipping = (productData?.shipping && typeof productData.shipping === 'object')
          ? productData.shipping
          : {
              'Processing time': '1–2 business days',
              'Standard shipping': '3–7 business days',
              'Returns': '30 days from delivery',
            };
        const entries = Object.entries(shipping);
        return (
          <div className="space-y-4">
            {entries.map(([key, value]) => (
              <div key={key} className="flex justify-between py-2 border-b border-border last:border-b-0">
                <span className="font-medium text-foreground">{key}</span>
                <span className="text-muted-foreground text-right">{String(value)}</span>
              </div>
            ))}
          </div>);
      }


      default:
        return null;
    }
  };

  return (
    <div className="bg-card rounded-2xl border border-border shadow-elevation-1">
      {/* Tab Navigation */}
      <div className="border-b border-border">
        <div className="flex overflow-x-auto">
          {tabs?.map((tab) =>
          <button
            key={tab?.id}
            onClick={() => setActiveTab(tab?.id)}
            className={`flex items-center space-x-2 px-6 py-4 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
            activeTab === tab?.id ?
            'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`
            }>

              <Icon name={tab?.icon} size={16} />
              <span>{tab?.label}</span>
              {tab?.count &&
            <span className="bg-muted text-muted-foreground text-xs px-2 py-1 rounded-full">
                  {tab?.count}
                </span>
            }
            </button>
          )}
        </div>
      </div>
      {/* Tab Content */}
      <div className="p-6">
        {renderTabContent()}
      </div>
    </div>);

};

const DEFAULT_SIZE_CHART = {
  unit: 'in',
  rows: [
    { size: 'S',    bust: 34, waist: 26, hip: 36, length: 24, shoulder: 14 },
    { size: 'M',    bust: 36, waist: 28, hip: 38, length: 25, shoulder: 14.5 },
    { size: 'L',    bust: 38, waist: 30, hip: 40, length: 26, shoulder: 15 },
    { size: 'XL',   bust: 40, waist: 32, hip: 42, length: 27, shoulder: 15.5 },
    { size: 'XXL',  bust: 42, waist: 34, hip: 44, length: 28, shoulder: 16 },
    { size: 'XXXL', bust: 44, waist: 36, hip: 46, length: 29, shoulder: 16.5 },
  ],
};

function SizeChartGrid({ sizeChart }) {
  const data = (sizeChart && Array.isArray(sizeChart.rows) && sizeChart.rows.length)
    ? sizeChart
    : DEFAULT_SIZE_CHART;
  const baseUnit = (data.unit || 'in').toLowerCase();
  const [unit, setUnit] = useState(baseUnit);

  const convert = (val) => {
    if (val == null || val === '') return '';
    const n = typeof val === 'number' ? val : parseFloat(val);
    if (Number.isNaN(n)) return String(val);
    if (unit === baseUnit) return Number.isInteger(n) ? n : n.toFixed(1);
    // base → other unit
    const out = baseUnit === 'in' ? n * 2.54 : n / 2.54;
    return out.toFixed(1);
  };

  // Discover column order from the first row (excluding the "size" label)
  const first = data.rows[0] || {};
  const cols = Object.keys(first).filter(k => k !== 'size');
  const colLabel = (k) => k.charAt(0).toUpperCase() + k.slice(1);

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-medium text-foreground">Size Chart</h4>
        <div className="inline-flex rounded-md border border-border overflow-hidden text-xs">
          {['in','cm'].map(u => (
            <button
              key={u}
              type="button"
              onClick={() => setUnit(u)}
              className={`px-3 py-1 transition-colors ${unit === u ? 'bg-primary text-primary-foreground' : 'bg-card text-muted-foreground hover:bg-muted'}`}
            >
              {u.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-muted/60">
              <th className="text-left font-medium text-foreground px-3 py-2 border border-border">Size</th>
              {cols.map(c => (
                <th key={c} className="text-left font-medium text-foreground px-3 py-2 border border-border">
                  {colLabel(c)} ({unit})
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.rows.map((row) => (
              <tr key={row.size} className="hover:bg-muted/40">
                <td className="px-3 py-2 border border-border font-medium text-foreground">{row.size}</td>
                {cols.map(c => (
                  <td key={c} className="px-3 py-2 border border-border text-muted-foreground">{convert(row[c])}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="mt-3 text-xs text-muted-foreground">
        Measurements are body measurements, not garment measurements. For the best fit, compare to a garment you already own and like.
      </p>

      <div className="mt-4 p-4 bg-accent/10 border border-accent/20 rounded-lg">
        <div className="flex items-start space-x-2">
          <Icon name="Info" size={16} className="text-accent flex-shrink-0 mt-0.5" />
          <p className="text-sm text-accent-foreground">
            Between sizes? Size up for a relaxed fit, down for a closer fit. Still unsure — reach out before you order.
          </p>
        </div>
      </div>
    </div>
  );
}

export default ProductTabs;